import { Component, Input, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AIMode } from 'src/app/models/user.interface';
import { IPostUpdate } from 'src/app/models/post-update.interface';
import { SelectedPublishTarget } from 'src/app/models/selected-publish-target.interface';
import { OpenAiService } from 'src/app/services/openai.service';
import { EditSocialCardComponent } from 'src/app/components/edit-social-card/edit-social-card.component';
import { OpenAiSummarizeResponse } from 'src/app/models/openai/openai-summarized-response.interface';

@Component({
  selector: 'app-publish-target-summaries',
  templateUrl: './publish-target-summaries.component.html',
  styleUrls: ['./publish-target-summaries.component.scss'],
  standalone: false,
})
export class PublishTargetSummariesComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() targets: SelectedPublishTarget[] = [];
  @Input() post: IPostUpdate;
  @Input() plainBody?: string;
  @Input() directions: string;
  @Input() aiMode: AIMode = 'ai_auto';

  // Image bindings for the slider
  @Input() selectedImage?: string;

  @ViewChild(EditSocialCardComponent) editSocialCardComponent: EditSocialCardComponent;

  isLoading = false;
  results: SelectedPublishTarget[] = [];
  socialCardImage?: string | null;

  editState: { [targetId: string]: { [postIndex: number]: string } } = {}; // Track edits for each post
  originalState: { [targetId: string]: { [postIndex: number]: string } } = {}; // Track original post values
  loadingStates: { [targetId: string]: boolean } = {};

  wordpressTarget: SelectedPublishTarget = undefined;

  // Helper: strip HTML tags to get plain text. Uses DOM if available, falls back to regex.
  private stripHtml(input: string): string {
    if (!input) return '';
    try {
      if (typeof document !== 'undefined') {
        const d = document.createElement('div');
        d.innerHTML = input;
        return (d.textContent || d.innerText || '').trim();
      }
    } catch (e) {
      // fall through to regex
    }
    return input.replace(/<[^>]*>/g, '').replace(/&nbsp;|&amp;|&lt;|&gt;/g, ' ').trim();
  }

  private getPlainBody(): string {
    if (this.plainBody && this.plainBody.trim().length > 0) return this.plainBody.trim();
    if (this.post && typeof this.post.body === 'string' && this.post.body.trim().length > 0) {
      return this.stripHtml(this.post.body);
    }
    return '';
  }

  constructor(
    private openai: OpenAiService,
    private modalController: ModalController
  ) {}

  /**
   *
   * @returns
   */
  async ngOnInit() {
    // Store the wordpress target if it exists.
    // Ensure selectedImage or social card are set from the post if not provided as inputs
    if (!this.selectedImage && this.post?.image) {
      this.selectedImage = this.post.image as string;
    }
    if (this.getSocialCardPreviewUrl()) {
      this.socialCardImage = this.getSocialCardPreviewUrl();
    }

    this.wordpressTarget = this.targets.find(
      (target) => target.id === 'wordpress'
    );
    if (this.wordpressTarget) {
      this.wordpressTarget.summaryType = 'none';
    }

    if (!this.targets.length) {
      console.error(
        'No targets to summarize. Ensure modal inputs are properly passed.'
      );

      // Dismiss the modal with an error message
      this.modalController.dismiss({
        success: false,
        error: 'No targets provided for summarization.',
      });
      return;
    }

    // Default behavior: AI enabled -> summarize automatically
    await this.summarizeAllTargets();
  }

  ngAfterViewInit(): void {
    // If it's a social card post and we don't have an image yet, generate one.
    if (
      this.post?.postType === 'social-card' &&
      !this.getSocialCardPreviewUrl() &&
      this.editSocialCardComponent
    ) {
      this.editSocialCardComponent.generateCard();
    }
  }

  ngOnDestroy(): void {
    //this.clearModalState(); // Clear all component state
    this.targets = [];
    this.post = null;
    this.directions = null;

    this.isLoading = false;
    //results: SelectedPublishTarget[] = [];

    this.editState = {};
    this.originalState = {};
    this.loadingStates = {};

    this.wordpressTarget = undefined;
  }

  /**
   *
   */
  async summarizeAllTargets() {
    this.isLoading = true;

    try {

// DEBUG: explain why each target will be included/excluded by the filter
const _plainForDebug = this.getPlainBody();
const debugTargets = (this.targets || []).map(t => {
  const socialMedia = t?.socialMedia;
  const socialMediaType = typeof socialMedia;
  const summaryTypeRaw = (t?.summaryType ?? '').toString();
  const summaryType = summaryTypeRaw.trim().toLowerCase();
  const accepted = !!t?.accepted;
  const characterLimit = t?.characterLimit;
  const plainLen = (_plainForDebug || '').length;

  let reason = 'include';
  if (accepted) reason = 'excluded: accepted';
  else if (socialMedia !== true) reason = `excluded: socialMedia !== true (value=${JSON.stringify(socialMedia)})`;
  else if (summaryType === 'full' || summaryType === 'none') reason = `excluded: summaryType=${summaryTypeRaw}`;
  else if (characterLimit && plainLen <= characterLimit) reason = `excluded: plain length ${plainLen} <= characterLimit ${characterLimit}`;

  return {
    id: t?.id,
    socialMedia,
    socialMediaType,
    summaryTypeRaw,
    summaryType,
    accepted,
    characterLimit,
    plainLen,
    reason
  };
});

console.table(debugTargets);
console.log('Will be summarized (ids):', debugTargets.filter(d => d.reason === 'include').map(d => d.id));

      // Filter targets for summarization
      const targets = this.targets.filter((target: SelectedPublishTarget) => {
        // Exclude already accepted targets
        if (target.accepted) {
          return false;
        }

        // Skip targets with socialMedia = false or summaryType = 'full' or 'none'
        if (!target.socialMedia || target.summaryType === 'full' || target.summaryType === 'none') {
          return false;
        }
        // Skip if body text is already below the character limit (use plain text)
        const _plain = this.getPlainBody();
        if (_plain && target.characterLimit && _plain.length <= target.characterLimit) {
          return false;
        }
        // Include all other cases
        return true;
      });

      // Mirrors the API logic to avoid unnecessary AI calls for very short content.
      const bodyTextForShortCheck = String((this.getPlainBody() || this.post?.title || '') || '').trim();
      const wordCount = bodyTextForShortCheck ? bodyTextForShortCheck.split(/\s+/).filter(Boolean).length : 0;
      const charCount = bodyTextForShortCheck.length || 0;
      const SHORT_CHAR_THRESHOLD = 150;
      const SHORT_WORD_THRESHOLD = 20;

      if (charCount > 0 && charCount <= SHORT_CHAR_THRESHOLD && wordCount <= SHORT_WORD_THRESHOLD) {
        // Check every relevant target (social media targets that are not 'full'/'none')
        const relevantTargets = this.targets.filter(t => t.socialMedia && t.summaryType !== 'full' && t.summaryType !== 'none');
        const fitsAll = relevantTargets.every(t => {
          const limit = ((t as any).firstPostLimit ?? t.characterLimit) ?? Infinity;
          return charCount <= (limit || Infinity);
        });

        if (fitsAll) {
          this.results = this.targets.map((target: SelectedPublishTarget): SelectedPublishTarget => ({
            ...target,
            posts: [{
              text: bodyTextForShortCheck,
              characters: charCount,
              generatedLink: this.post?.featuredUrl
            }],
            error: null,
            ai: false,
            accepted: target.accepted,
          })).filter((target) => target.showOnSummarize !== false);

          this.validateCharacterLimits(this.results);
          this.isLoading = false;
          return;
        }
      }

      // ** Skip summarization if no targets to summarize **
      if (targets.length === 0) {
        // Still populate results to include all original targets with error messages or empty states
          const plainLen = this.getPlainBody().length;
          this.results = this.targets.map((target): SelectedPublishTarget => ({
          ...target,
          error: target.characterLimit && plainLen <= target.characterLimit
            ? `Body text is already within the character limit (${target.characterLimit})`
            : null,
          ai: false,
          posts: target.posts || [],
          accepted: target.accepted,
        })).filter((target) => target.showOnSummarize !== false);

        this.validateCharacterLimits(this.results);
        this.isLoading = false; // Stop the loading state
        return;
      }

      // If AI mode is enabled, call the OpenAI service to summarize
      if (this.aiMode === 'ai_auto') {

        const response: OpenAiSummarizeResponse = await this.openai.summarizeMultiple(targets, this.post, this.directions).toPromise();

        // Debug: log raw API response
        console.debug('[PublishTargetSummaries] API response summaries:', response?.summaries);

        // Transform the response to match `SelectedPublishTarget`
        const newResults = this.targets.map((target): SelectedPublishTarget => {
          const summary = response.summaries.find((t) => t.id === target.id);
          if (summary) {
            return {
              ...target,
              posts: summary.posts.map((post) => ({
                text: post.text,
                characters: post.characters,
                generatedLink: post.generatedLink,
              })),
              error: null,
              ai: true,
              accepted: target.accepted,
            };
          }

          return {
            ...target,
            error: target.characterLimit && this.getPlainBody().length <= target.characterLimit
              ? `Body text is already within the character limit (${target.characterLimit})`
              : null,
            ai: false,
            posts: target.posts || [],
            accepted: target.accepted,
          } as SelectedPublishTarget;
        });

        // Debug: log mapped results before filtering
        console.debug('[PublishTargetSummaries] Mapped newResults (pre-filter):', newResults);

        // Filter out only targets that should be displayed
        this.results = newResults.filter(
          (target) =>
            target.showOnSummarize !== false &&
            target.socialMedia && target.summaryType !== 'full' && target.summaryType !== 'none'
        );

        // Quick verification fallback: if any mapped post text is shorter than the corresponding API text, log it.
        try {
          response.summaries.forEach((s) => {
            const mapped = newResults.find(n => n.id === s.id);
            if (mapped && mapped.posts && s.posts) {
              s.posts.forEach((apiPost, idx) => {
                const mappedPost = mapped.posts[idx];
                if (mappedPost && apiPost.text && mappedPost.text && mappedPost.text.length < apiPost.text.length) {
                  console.warn(`[PublishTargetSummaries] Truncation detected for target ${s.id} post ${idx}: api=${apiPost.text.length} mapped=${mappedPost.text.length}`);
                }
              });
            }
          });
        } catch (e) {
          console.error('[PublishTargetSummaries] Error during post-length verification', e);
        }
      }

      // Add previously accepted or "full"-summary targets back into results (no duplicates).
      // Simple resolver: prefer `target.posts` when present; otherwise fall back
      // to a single post built from the plain body. Keep logic data-driven
      // and avoid special-casing specific publish targets.
      const resolvePostsFor = (target: SelectedPublishTarget) => {
        if (target.posts && target.posts.length) return target.posts;
        const plain = this.getPlainBody();
        return [{ text: plain, characters: (plain || '').length, generatedLink: this.post?.featuredUrl }];
      };

      this.targets
        .filter((target) => (target.accepted || target.summaryType === 'full' || target.summaryType === 'none') && target.showOnSummarize !== false)
        .forEach((target: SelectedPublishTarget) => {
          const existingTarget = this.results.find((r) => r.id === target.id);
          const postsToUse = resolvePostsFor(target);

          if (existingTarget) {
            existingTarget.accepted = true;
            existingTarget.posts = postsToUse;
          } else {
            this.results.push({
              ...target,
              posts: postsToUse,
              accepted: true,
              error: null,
            });
          }
        });

      // Validate character limits
      this.validateCharacterLimits(this.results);

    } catch (error: any) {
      console.error('Error summarizing targets:', error);
      this.modalController.dismiss({
        success: false,
        error: 'Failed to summarize targets.',
        logId: error?.error?.logId || null
      });
    } finally {
      this.isLoading = false;
    }
  }


  /**
   * Chunk without AI
   */
  chunkTargets() {
    const plain = this.getPlainBody();
    this.results = this.targets.map(target => {
      // If thread is explicitly false we should NOT chunk — keep original post as single item.
      if (target.thread === false) {
        return {
          ...target,
          posts: [{
            text: plain,
            characters: plain.length,
            generatedLink: this.post.featuredUrl
          }],
          ai: false,
          error: '',
          accepted: false
        };
      }

      // If no char limit or post is within limit, just use the original post
      if (!target.characterLimit || plain.length <= target.characterLimit) {
        return {
          ...target,
          posts: [{
            text: plain,
            characters: plain.length,
            generatedLink: this.post.featuredUrl
          }],
          ai: false,
          error: '',
          accepted: false
        };
      }

      // Chunking logic
      const words = plain.split(/\s+/);
      const chunks: string[] = [];
      let currentChunk = '';
      let chunkIndex = 1;

      for (let i = 0; i < words.length; i++) {
        // Predict the next chunk with the next word and the chunk index suffix
        const nextWord = words[i];
        const suffix = ` /${chunkIndex}`;
        // +1 for space if not first word
        const nextChunk = currentChunk.length > 0 ? `${currentChunk} ${nextWord}` : nextWord;
        // Check if adding this word and suffix would exceed the limit
        if ((nextChunk + suffix).length > target.characterLimit) {
          // Push the current chunk with suffix
          chunks.push(currentChunk + suffix);
          currentChunk = nextWord;
          chunkIndex++;
        } else {
          currentChunk = nextChunk;
        }
      }
      // Push the last chunk
      if (currentChunk.length > 0) {
        chunks.push(currentChunk + ` /${chunkIndex}`);
      }

      return {
        ...target,
        posts: chunks.map(chunk => ({
          text: chunk,
          characters: chunk.length,
          generatedLink: this.post.featuredUrl
        })),
        ai: false,
        error: null,
        accepted: false
      };
    });

    // Validate character limits for all results
    this.validateCharacterLimits(this.results);
  }


  /**
   * Restore the original summary for a specific target.
   * @param target 
   */
  async originalSummary(target: SelectedPublishTarget) {
    try {
      // Revert the target's posts to the original post body
      const plain = this.getPlainBody();
      target.posts = [{
        text: plain,
        characters: plain.length,
        generatedLink: this.post.featuredUrl
      }];
      target.ai = false;
      target.error = null;

      // Update the results array so the UI reflects the change
      const resultTarget = this.results.find(t => t.id === target.id);
      if (resultTarget) {
        resultTarget.posts = [...target.posts];
        resultTarget.ai = false;
        resultTarget.error = null;
      }

      // Validate character limits for this target
      this.validateCharacterLimits([target]);
    } catch (error) {
      console.error(`Error reverting summary for ${target.id}:`, error);
      target.error = 'Failed to revert';
    }
  }


  /**
   * Redo single
   * @param target 
   */
  async redoSummary(target: SelectedPublishTarget) {
    this.loadingStates[target.id] = true; // Start target-specific loading state

    try {
      const payload = {
        targets: [target],
        post: { ...this.post, body: this.getPlainBody() },
      };

      if (this.wordpressTarget) {
        payload.targets.push(this.wordpressTarget);
      }

      target.accepted = false;

      const response: OpenAiSummarizeResponse = await new Promise((resolve, reject) => {
        this.openai.summarizeMultiple(payload.targets, payload.post, this.directions).subscribe(
          (res) => {
            if (!res.success) {
              return reject(new Error(res.message || 'Redo summarization failed.'));
            }
            resolve(res);
          },
          (err) => reject(err)
        );
      });

      const updatedTarget = response.summaries.find((t) => t.id === target.id);
      if (updatedTarget) {
        target.posts = updatedTarget.posts.map((post) => ({
          text: post.text,
          characters: post.characters,
          generatedLink: post.generatedLink, // Preserve the generated link property
        }));
        target.error = null;
      } else {
        console.error(`Redo failed: No updated summary for target ${target.id}`);
        target.error = `Failed to retrieve updated summary for ${target.id}`;
      }

      // Validate character limits
      this.validateCharacterLimits(this.results);

    } catch (error) {
      console.error(`Error redoing summary for ${target.id}:`, error);
      target.error = 'Failed to summarize';
    } finally {
      this.loadingStates[target.id] = false; // End target-specific loading state
    }
  }


  /**
 * Validate character limits for each target's posts.
 * Adds an error to the target if any post exceeds the character limit.
 */
  validateCharacterLimits(targets: SelectedPublishTarget[]): boolean {
    let allWithinLimit = true;

    targets.forEach((target) => {
      if (target.characterLimit) {
        const invalidPosts = target.posts.filter((post) => {
          const textIsEmpty = !post.text || post.text.trim().length === 0; // Check for empty or whitespace-only text
          const linkLength = post.generatedLink ? post.generatedLink.length + 1 : 0;
          const charCount = post.text.length + linkLength;
          const isOverCharLimit = charCount > target.characterLimit;

          // If either condition is true, the post is invalid
          return textIsEmpty || isOverCharLimit;
        });

        if (invalidPosts.length > 0) {
          const hasEmptyText = invalidPosts.some(post => !post.text || post.text.trim().length === 0);
          const hasOverCharLimit = invalidPosts.some(post => {
            const linkLength = post.generatedLink ? post.generatedLink.length + 1 : 0;
            const charCount = post.text.length + linkLength;
            return charCount > target.characterLimit;
          });

          if (hasEmptyText) {
            target.error = 'One or more posts are empty.';
            allWithinLimit = false;
          } else if (hasOverCharLimit) {
            target.error = `One or more posts exceed the character limit of ${target.characterLimit} characters.`;
            allWithinLimit = false;
          }
        } else {
          target.error = null; // Clear any previous errors if validation passes
        }
      }
    });

    return allWithinLimit;
  }


  /**
   * Get character count
   * @param target 
   * @param postIndex 
   * @returns 
   */
  getCharacterCountColor(target: SelectedPublishTarget, postIndex: number): string {
    const postText = this.editState[target.id]?.[postIndex] || target.posts[postIndex]?.text || '';
    const linkLength = target.posts[postIndex]?.generatedLink ? target.posts[postIndex].generatedLink.length + 1 : 0;
    const charCount = postText.length + linkLength;

    if (!target.characterLimit) return 'medium'; // No limit, neutral color
    if (charCount > target.characterLimit) return 'danger'; // Over the limit
    if (charCount / target.characterLimit >= 0.9) return 'warning'; // Approaching the limit
    return 'medium'; // Within safe range
  }


  startEditing(targetId: string, postIndex: number) {
    if (!this.editState[targetId]) {
      this.editState[targetId] = {};
    }
    if (!this.originalState[targetId]) {
      this.originalState[targetId] = {};
    }
    // Check if the edit state for the specific post index exists
    if (!this.editState[targetId].hasOwnProperty(postIndex)) {
      const target = this.results.find((t) => t.id === targetId);
      const postText = target?.posts[postIndex]?.text ?? ''; // Use ?? to ensure an empty string if post text is null/undefined
      this.originalState[targetId][postIndex] = postText; // Store original value
      this.editState[targetId][postIndex] = postText; // Store initial editing state (even if empty)
    }
  }



  isEditing(targetId: string, postIndex: number): boolean {
    return this.editState[targetId] && this.editState[targetId].hasOwnProperty(postIndex);
  }


  isTargetBeingEdited(targetId: string): boolean {
    return Object.keys(this.editState[targetId] || {}).some((index) =>
      this.editState[targetId].hasOwnProperty(index)
    );
  }


  updateCharacterCount(target: SelectedPublishTarget, postIndex: number) {
  }


  /**
   * Is over charcater limit
   * @param target 
   * @param postIndex 
   * @returns 
   */
  isOverCharacterLimit(target: SelectedPublishTarget, postIndex: number): boolean {
    const postText = this.editState[target.id]?.[postIndex] || target.posts[postIndex]?.text || '';
    const linkLength = target.posts[postIndex]?.generatedLink ? target.posts[postIndex].generatedLink.length + 1 : 0;
    const charCount = postText.length + linkLength;
    return target.characterLimit ? charCount > target.characterLimit : false;
  }


  /**
   * Determine if the accept button should be disabled.
   * @param target target
   * @returns boolean
   */
  allowAccept(target: SelectedPublishTarget): boolean {
    // Disable if target has an error or is already accepted
    return !!target.error || target.accepted;
  }


  /**
   * 
   * @param target 
   */
  acceptColor(target: SelectedPublishTarget): string {
    if (target.error) {
      return "warning";
    }
    return "success";
  }


  /**
 * Handle text changes in the ion-textarea.
 * @param targetId The ID of the target being edited.
 * @param postIndex The index of the post being edited.
 */
  handleEditChange(targetId: string, postIndex: number) {
    const target = this.results.find((t) => t.id === targetId);
    if (target) {
      // Update the post array in real time
      target.posts[postIndex].text = this.editState[targetId][postIndex];
      this.validateCharacterLimits([target]);
    }
  }


  cancelEdit(targetId: string, postIndex: number) {
    const originalText = this.originalState[targetId]?.[postIndex];
    if (originalText !== undefined) {
      const target = this.results.find((t) => t.id === targetId);
      if (target) {
        target.posts[postIndex].text = originalText; // Reset the text to its original value
        this.validateCharacterLimits([target]);
      }
    }
    delete this.editState[targetId][postIndex]; // Exit editing mode
    delete this.originalState[targetId][postIndex]; // Clear the stored original value
  }


  saveEdit(targetId: string, postIndex: number) {
    const target = this.results.find((t) => t.id === targetId);
    if (target) {
      target.posts[postIndex].text = this.editState[targetId][postIndex];
      if (this.validateCharacterLimits([target])) {
        this.acceptSummary(target);
      }
    }
    delete this.editState[targetId][postIndex];
  }


  /**
 * Deletes a post from the target's posts array.
 * @param targetId The ID of the target containing the post to delete.
 * @param postIndex The index of the post to delete.
 */
  deletePost(targetId: string, postIndex: number) {
    const target = this.results.find((t) => t.id === targetId);
    if (target) {
      target.posts.splice(postIndex, 1); // Remove the post at the specified index
      target.error = null; // Clear any existing errors, as the user might correct them by deleting

      delete this.editState[targetId][postIndex]; // Exit editing mode
    } else {
      console.error(`Target ${targetId} not found.`);
    }
  }


  /**
   * Accept summary for target
   * @param target 
   */
  acceptSummary(target: SelectedPublishTarget) {
    target.accepted = true;
  }


  /**
   * Track by index for ngFor
   * @param index 
   * @param _obj 
   * @returns 
   */
  trackByIndex(index: number, _obj: any): number {
    return index;
  }


  /**
 * Check if any targets have errors
 * @returns boolean - True if any targets have errors, false otherwise
 */
  hasErrors(): boolean {
    return this.results.some(target => !!target.error);
  }


  /**
   * Accept all
   */
  acceptAll() {
    this.results.forEach((target) => {
      target.accepted = true;
    });

    const acceptedSummaries = this.results;
    this.modalController.dismiss({ acceptedSummaries, success: true, reason: 'accept' });
  }


  /**
   * Close modal
   */
  close() {
    // Filter only the accepted summaries
    const acceptedSummaries = this.results.filter(target => target.accepted);
    this.modalController.dismiss({ acceptedSummaries, success: false, reason: 'close' });
  }


  /**
   * Check if social card preview should be shown
   */
  shouldShowSocialCardPreview(): boolean {
    // Check if any target has useSocialCard enabled
    const useSocialCard = this.targets.some(t => t.useSocialCard);

    if (!useSocialCard) {
      return false;
    }
    // And check if a social card image has been generated
    if (this.post?.socialCard?.images && Object.keys(this.post.socialCard.images).length > 0) {
      return true;
    }
    return false;
  }


  getSocialCardPreviewUrl(): string | undefined {
    if (this.post?.socialCard?.images) {
      // Prefer landscape for a generic preview, otherwise take the first available one.
      return this.post?.socialCard.images['landscape'] || this.post?.socialCard.images['portrait'] || Object.values(this.post?.socialCard.images)[0];
    }
    return undefined;
  }

  /**
   * Return true when either a selected image or any socialCard images exist.
   * Template calls should use this instead of referencing `Object` directly.
   */
  hasImageAvailable(): boolean {
    if (this.selectedImage) return true;
    const images = this.post?.socialCard?.images;
    if (!images) return false;
    try {
      return Object.keys(images).length > 0;
    } catch (e) {
      return false;
    }
  }

  rebuildSocialCardImage() {
    if (this.editSocialCardComponent) {
      this.editSocialCardComponent.generateCard();
    }
  }
}

